// Video offline cache — the FULL file, fetched once, so the Service Worker
// can answer the <video> element's Range requests from it.
//
// Why this exists: a <video> only ever asks for byte ranges (206 Partial
// Content). Workbox's range handling slices a cached FULL response; it cannot
// build one from partial responses, and it cannot read an opaque one at all.
// Left to runtime caching, the SW stored whatever the element happened to
// request — no-cors, so opaque (status 0), possibly a partial body — and then
// replayed it as if it were the whole file: the video played once from the
// network and failed with "Format error" on every later play from cache.
//
// So videos are downloaded here, in CORS mode, as one complete 200 response
// into the cache the SW's video route reads ('wt-video', vite.config.ts).
// The key is the same absoluteUrl() VideoDisplay plays, so the SW finds it.
// Best-effort: a failure leaves the video streaming from the network, exactly
// as before; it never blocks playback.

import { absoluteUrl, isVideoUrl } from './contentUrl'

export const VIDEO_CACHE = 'wt-video'

const inFlight = new Set<string>()

/** Every video URL anywhere in a content manifest (items, zones, overlays). */
function collectVideoUrls(node: unknown, out: Set<string>): Set<string> {
  if (Array.isArray(node)) {
    for (const v of node) collectVideoUrls(v, out)
  } else if (node && typeof node === 'object') {
    for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
      if (k === 'url' && typeof v === 'string' && isVideoUrl(v)) out.add(absoluteUrl(v))
      else collectVideoUrls(v, out)
    }
  }
  return out
}

export function warmVideoCache(content: unknown): void {
  if (typeof caches === 'undefined') return
  const urls = [...collectVideoUrls(content, new Set())].filter(u => u && !inFlight.has(u))
  if (!urls.length) return
  void (async () => {
    let cache: Cache
    try { cache = await caches.open(VIDEO_CACHE) } catch { return }
    // One at a time: a screen's bandwidth goes to what is playing now.
    for (const url of urls) {
      inFlight.add(url)
      try {
        if (await cache.match(url)) continue
        const res = await fetch(url, { mode: 'cors', credentials: 'omit' })
        // Only a complete, readable 200 — never a 206 or an opaque response.
        if (res.status === 200 && res.type !== 'opaque') await cache.put(url, res)
      } catch {
        /* offline or blocked: it streams from the network when played */
      } finally {
        inFlight.delete(url)
      }
    }
  })()
}
